import React, { useRef, useState } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { formatAppDate } from "../../../shared/utils/dateUtils.js";
import { numberToIndianWords } from "../../../shared/utils/numberToWords.js";
import { shareReceiptPdf } from "../../../shared/utils/pdfShare.js";
import { useCompanies } from "../../companies/hooks/useCompanies.js";

export function PaymentReceiptModal({
  isOpen,
  onClose,
  payment
}) {
  const { defaultCompany } = useCompanies();
  const printableRef = useRef(null);
  const [isSharing, setIsSharing] = useState(false);

  if (!isOpen || !payment) return null;

  const isPaymentIn = payment.type !== "PAYMENT_OUT";
  const voucherTitle = isPaymentIn ? "PAYMENT RECEIPT" : "PAYMENT VOUCHER";
  const partyLabel = isPaymentIn ? "Received with thanks from:" : "Paid to:";
  const partyName = payment.customerName || payment.vendorName || payment.partyName || "Party";

  const companyName = defaultCompany?.name || "SREE RAM TRADERS";
  const companyGstin = defaultCompany?.gstin || "33AWFPA8086P1Z3";
  const companyAddress = defaultCompany?.address || "33-C5, Pambalamman kovil street, Chettiyarpatti-626122";
  const companyPhone = defaultCompany?.phone || "9865460487";
  const companyEmail = defaultCompany?.email || "admin@webs8.com";
  const companyDateFormat = defaultCompany?.dateFormat || "DD-MM-YYYY";

  const formattedDate = formatAppDate(payment.paymentDate, companyDateFormat);
  const amountNum = Number(payment.amount || 0);
  const wordsAmount = numberToIndianWords(amountNum);
  const allocations = payment.allocations || [];
  const isVoid = payment.status === "VOID";

  const handlePrint = () => {
    window.print();
  };

  const handleShareWhatsApp = async () => {
    if (!printableRef.current) return;
    setIsSharing(true);
    try {
      const shareData = {
        ...payment,
        customerPhone: payment.customerPhone || payment.vendorPhone || payment.partyPhone || ""
      };
      await shareReceiptPdf(printableRef.current, shareData, companyName);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Receipt PDF share error:", err);
        alert("Failed to share receipt PDF: " + err.message);
      }
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={`📄 ${voucherTitle}: ${payment.paymentNo || "Receipt"}`}
      size="lg"
      className="receipt-modal"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

        {isVoid && (
          <div style={{ background: "rgba(239, 68, 68, 0.12)", border: "1px solid rgba(239, 68, 68, 0.35)", padding: "0.6rem 0.85rem", borderRadius: "var(--radius-md)", color: "#fca5a5", fontSize: "0.85rem" }}>
            🚫 This {isPaymentIn ? "receipt" : "voucher"} was voided{payment.voidReason ? `: ${payment.voidReason}` : "."}
          </div>
        )}

        {/* Printable Receipt Canvas */}
        <div
          ref={printableRef}
          className="printable-receipt-card"
          style={{
            background: "#ffffff",
            color: "#0f172a",
            border: "1px solid #cbd5e1",
            borderRadius: "12px",
            padding: "1.5rem",
            boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
            fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            position: "relative",
            overflow: "hidden"
          }}
        >
          {isVoid && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(-25deg)",
                fontSize: "6rem",
                fontWeight: "900",
                letterSpacing: "0.5rem",
                color: "rgba(220, 38, 38, 0.18)",
                border: "8px solid rgba(220, 38, 38, 0.18)",
                borderRadius: "12px",
                padding: "0 1.5rem",
                pointerEvents: "none",
                zIndex: 1
              }}
            >
              VOID
            </div>
          )}
          {/* Top Company Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #0284c7", paddingBottom: "0.85rem", marginBottom: "1rem" }}>
            <div>
              <h2 style={{ fontSize: "1.35rem", fontWeight: "800", color: "#0f172a", margin: 0, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {companyName}
              </h2>
              <div style={{ fontSize: "0.8rem", color: "#475569", marginTop: "3px", lineHeight: "1.4" }}>
                {companyAddress}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#475569" }}>
                📞 {companyPhone} {companyEmail ? `• ✉️ ${companyEmail}` : ""}
              </div>
              {companyGstin && (
                <div style={{ fontSize: "0.82rem", fontWeight: "700", color: "#0284c7", marginTop: "2px" }}>
                  GSTIN: {companyGstin}
                </div>
              )}
            </div>

            <div style={{ textAlign: "right" }}>
              <span style={{ display: "inline-block", background: "#f0f9ff", color: "#0284c7", border: "1px solid #bae6fd", padding: "4px 12px", borderRadius: "6px", fontWeight: "800", fontSize: "0.9rem", letterSpacing: "0.5px" }}>
                {voucherTitle}
              </span>
              <div style={{ fontSize: "0.95rem", fontWeight: "800", color: "#0f172a", marginTop: "6px" }}>
                #{payment.paymentNo || "RCP-001"}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "2px" }}>
                📅 Date: <strong>{formattedDate}</strong>
              </div>
            </div>
          </div>

          {/* Party & Payment Meta Box */}
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "1rem", background: "#f8fafc", padding: "0.85rem 1rem", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "1rem" }}>
            <div>
              <div style={{ fontSize: "0.72rem", textTransform: "uppercase", fontWeight: "700", color: "#64748b" }}>
                {partyLabel}
              </div>
              <div style={{ fontSize: "1.1rem", fontWeight: "800", color: "#0f172a", marginTop: "2px" }}>
                {partyName}
              </div>
              {payment.customerPhone && (
                <div style={{ fontSize: "0.8rem", color: "#475569", marginTop: "2px" }}>
                  📞 {payment.customerPhone}
                </div>
              )}
            </div>

            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>Payment Mode</div>
              <div style={{ fontSize: "0.92rem", fontWeight: "700", color: "#0f172a" }}>
                {payment.paymentMode || "Cash"}
              </div>
              {payment.reference && (
                <div style={{ fontSize: "0.8rem", color: "#475569", marginTop: "2px" }}>
                  Ref / UTR: <strong>{payment.reference}</strong>
                </div>
              )}
            </div>
          </div>

          {/* Invoices Settled Table */}
          <div style={{ marginBottom: "1rem" }}>
            <div style={{ fontSize: "0.75rem", fontWeight: "700", textTransform: "uppercase", color: "#64748b", marginBottom: "0.4rem", letterSpacing: "0.5px" }}>
              Settlement Allocation Details
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ background: "#f1f5f9", borderBottom: "2px solid #cbd5e1" }}>
                  <th style={{ padding: "8px 10px", textAlign: "left", color: "#334155", fontWeight: "700" }}>Bill / Invoice #</th>
                  <th style={{ padding: "8px 10px", textAlign: "left", color: "#334155", fontWeight: "700" }}>Bill Date</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", color: "#334155", fontWeight: "700" }}>Bill Total</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", color: "#334155", fontWeight: "700" }}>Amount Settled</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", color: "#334155", fontWeight: "700" }}>Remaining</th>
                </tr>
              </thead>
              <tbody>
                {allocations.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: "12px", textAlign: "center", color: "#64748b" }}>
                      Direct Payment (No invoice allocations)
                    </td>
                  </tr>
                ) : (
                  allocations.map((alloc, idx) => (
                    <tr key={alloc.invoiceId || alloc.purchaseId || idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "8px 10px", fontWeight: "700", color: "#0284c7" }}>
                        {alloc.invoiceNo || alloc.billNo || "Bill"}
                      </td>
                      <td style={{ padding: "8px 10px", color: "#475569" }}>
                        {formatAppDate(alloc.invoiceDate || alloc.billDate, companyDateFormat)}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: "#475569" }}>
                        ₹{Number(alloc.grandTotal || 0).toLocaleString("en-IN")}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: "700", color: "#059669" }}>
                        ₹{Number(alloc.allocatedAmount || 0).toLocaleString("en-IN")}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", color: Number(alloc.remainingBalance || 0) > 0 ? "#d97706" : "#64748b" }}>
                        ₹{Number(alloc.remainingBalance || 0).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Amount In Figures & Words */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderTop: "2px solid #0f172a", paddingTop: "0.85rem", marginTop: "0.5rem" }}>
            <div style={{ flex: 1, paddingRight: "1rem" }}>
              <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: "600" }}>
                Amount in Words:
              </div>
              <div style={{ fontSize: "0.9rem", fontWeight: "700", color: "#0f172a", marginTop: "2px", fontStyle: "italic" }}>
                "{wordsAmount}"
              </div>
              {payment.notes && (
                <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "6px" }}>
                  <strong>Notes:</strong> {payment.notes}
                </div>
              )}
              {Number(payment.unallocatedAmount || 0) > 0 && (
                <div style={{ fontSize: "0.8rem", color: "#d97706", fontWeight: "700", marginTop: "4px" }}>
                  ★ Unallocated Advance Credit: ₹{Number(payment.unallocatedAmount).toLocaleString("en-IN")}
                </div>
              )}
            </div>

            <div style={{ textAlign: "right", minWidth: "160px" }}>
              <div style={{ fontSize: "0.75rem", color: "#64748b", textTransform: "uppercase", fontWeight: "700" }}>
                Total Amount Received
              </div>
              <div style={{ fontSize: "1.45rem", fontWeight: "800", color: "#0284c7", marginTop: "2px" }}>
                ₹{amountNum.toLocaleString("en-IN")}
              </div>
            </div>
          </div>

          {/* Signatory Stamp */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "2rem", paddingTop: "1rem", borderTop: "1px dashed #cbd5e1" }}>
            <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>
              This is a computer-generated receipt.
            </div>
            <div style={{ textAlign: "center", minWidth: "160px" }}>
              <div style={{ height: "35px" }} />
              <div style={{ borderTop: "1px solid #475569", paddingTop: "4px", fontSize: "0.8rem", fontWeight: "700", color: "#334155" }}>
                For {companyName}
              </div>
              <div style={{ fontSize: "0.7rem", color: "#64748b" }}>
                Authorized Signatory
              </div>
            </div>
          </div>

        </div>

        {/* Action Controls Bar */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", borderTop: "1px solid var(--border-subtle)", paddingTop: "0.85rem", flexWrap: "wrap" }}>
          <Button type="button" variant="secondary" onClick={onClose} style={{ minHeight: "40px" }}>
            Close
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handlePrint}
            style={{ minHeight: "40px" }}
          >
            🖨️ Print Receipt
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleShareWhatsApp}
            loading={isSharing}
            style={{ minHeight: "40px", fontWeight: "700" }}
          >
            {isSharing ? "⏳ Generating PDF..." : "📲 Share PDF on WhatsApp"}
          </Button>
        </div>

      </div>
    </BottomSheet>
  );
}

export default PaymentReceiptModal;
